import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { DynamicInspectionService } from '@main-application/inspections/services/dynamic-inspection.service';
import { selectInspectionState } from '@main-application/inspections/store/inspections.selectors';
import getInspectionsSummaryFiltersKey from '@main-application/inspections/utils/inspection-mappers';
import { getTicketId } from '@main-application/service/service.constants';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  addInspectionTicket,
  addInspectionTicketFailed,
  addInspectionTicketSuccess,
  deleteDynamicInspection,
  deleteDynamicInspectionFailed,
  deleteDynamicInspectionSuccess,
  loadAllInspections,
  loadAllInspectionsFailed,
  loadAllInspectionsSuccess,
  loadDynamicInspections,
  loadDynamicInspectionsFailed,
  loadDynamicInspectionsSuccess,
  loadInReviewInspections,
  loadInReviewInspectionsFailed,
  loadInReviewInspectionsSuccess,
  loadInspectionSummaryReport,
  loadInspectionSummaryReportFailed,
  loadInspectionSummaryReportSucceeded,
  loadRepeatingInspections,
  loadRepeatingInspectionsFailed,
  loadRepeatingInspectionsSuccess,
  loadResidentInspections,
  loadResidentInspectionsFailed,
  loadResidentInspectionsSuccess,
  updateInspectionTicket,
  updateInspectionTicketFailed,
  updateInspectionTicketSuccess,
} from './inspections.actions';
import { InspectionService } from '../services/inspection.service';

@Injectable()
export class InspectionsEffects {
  loadAllInspections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllInspections),
      switchMap(action =>
        this.inspectionService
          .list(
            action.portfolioId,
            action.startDate,
            action.endDate,
            action.inspectionType,
            action.returnStandardAndResidentBoth,
            action.includeArchived
          )
          .pipe(
            map(result => loadAllInspectionsSuccess({ listItems: result || [] })),
            catchError(() => {
              this.snackbarService.error(SnackbarErrorMessage.LoadingInspectionsList);
              return of(loadAllInspectionsFailed());
            })
          )
      )
    )
  );

  loadResidentInspections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadResidentInspections),
      switchMap(action =>
        this.inspectionService.getResidentInspections().pipe(
          map(result => loadResidentInspectionsSuccess({ listItems: result || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingInspectionsList);
            return of(loadResidentInspectionsFailed());
          })
        )
      )
    )
  );

  loadInReviewInspections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInReviewInspections),
      switchMap(action =>
        this.inspectionService
          .inReviewList(
            action.portfolioId,
            action.isResident,
            action.returnStandardAndResidentBoth,
            action.includeArchived
          )
          .pipe(
            map(result => loadInReviewInspectionsSuccess({ inReviewListItems: result || [] })),
            catchError(() => {
              this.snackbarService.error(SnackbarErrorMessage.LoadingInspectionsList);
              return of(loadInReviewInspectionsFailed());
            })
          )
      )
    )
  );

  loadDynamicInspections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDynamicInspections),
      switchMap(action =>
        this.dynamicInspectionService.list(action.portfolioId).pipe(
          map(result => loadDynamicInspectionsSuccess({ listItems: result || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingInspectionsList);
            return of(loadDynamicInspectionsFailed());
          })
        )
      )
    )
  );

  loadRepeatingInspections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRepeatingInspections),
      switchMap(action =>
        this.inspectionService.repeating().pipe(
          map(result => loadRepeatingInspectionsSuccess({ listItems: result || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingInspectionsList);
            return of(loadRepeatingInspectionsFailed());
          })
        )
      )
    )
  );

  deleteDynamicInspection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDynamicInspection),
      switchMap(action =>
        this.dynamicInspectionService.delete(action.inspectionId).pipe(
          mergeMap(() => {
            this.snackbarService.success(`Dynamic ${action.stepName?.toUpperCase() || 'inspection'} deleted`);
            return [loadDynamicInspections({ portfolioId: action.portfolioId }), deleteDynamicInspectionSuccess()];
          }),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RemovingInspection);
            return of(deleteDynamicInspectionFailed());
          })
        )
      )
    )
  );

  addInspectionTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addInspectionTicket),
      switchMap(action =>
        this.inspectionService.createTicket(action.ticket).pipe(
          mergeMap(() => {
            this.snackbarService.success(`Ticket created`);
            return [addInspectionTicketSuccess()];
          }),
          catchError(err => {
            return of(addInspectionTicketFailed());
          })
        )
      )
    )
  );

  updateInspectionTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateInspectionTicket),
      switchMap(action =>
        this.inspectionService.updateTicket(action.ticket).pipe(
          mergeMap(result => {
            this.snackbarService.success(`Ticket #${getTicketId(action?.ticket)} updated`);
            return [updateInspectionTicketSuccess()];
          }),
          catchError(err => {
            return of(updateInspectionTicketFailed());
          })
        )
      )
    )
  );

  loadInspectionsSummaryReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInspectionSummaryReport),
      withLatestFrom(this.store.select(selectInspectionState)),
      switchMap(([action, state]) => {
        const filterKey = getInspectionsSummaryFiltersKey(action.filter);
        const cachedData = state.summaryStats[filterKey];

        if (cachedData) {
          return of(loadInspectionSummaryReportSucceeded({ payload: cachedData, filter: action.filter }));
        }

        return this.inspectionService.getSummaryStats(action.filter).pipe(
          map(result => loadInspectionSummaryReportSucceeded({ payload: result, filter: action.filter })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingInspectionsSummaryReport);
            return of(loadInspectionSummaryReportFailed());
          })
        );
      })
    )
  );

  constructor(
    private store: Store<{}>,
    private actions$: Actions,
    private snackbarService: SnackbarService,
    private inspectionService: InspectionService,
    private dynamicInspectionService: DynamicInspectionService
  ) {}
}
